import { create } from "zustand";

const ARRAY_LIST_KEYS = new Set(["textIncludes", "textExcludes"]);
const HEARTBEAT_TAG = "35=0";

const formatUtcNoMillis = (date) => date.toISOString().slice(0, 19);

export const getDefaultPast24hUtcRange = () => {
  const now = new Date();
  const past24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  return {
    timeFrom: formatUtcNoMillis(past24h),
    timeTo: formatUtcNoMillis(now),
  };
};

const sanitizeFilters = (filters = {}) => {
  const sanitized = {};
  Object.entries(filters).forEach(([key, value]) => {
    let processed = value;

    if (ARRAY_LIST_KEYS.has(key) && processed != null && !Array.isArray(processed)) {
      processed = [`${processed}`];
    }

    if (Array.isArray(processed)) {
      const arr = processed
        .map((item) => (item == null ? "" : `${item}`.trim()))
        .filter(Boolean);
      if (arr.length) sanitized[key] = arr;
      return;
    }

    if (processed != null && `${processed}`.trim() !== "") {
      sanitized[key] = processed;
    }
  });
  return sanitized;
};

const hasFilters = (filters = {}) =>
  Object.values(filters).some((value) =>
    Array.isArray(value) ? value.length > 0 : value != null && `${value}`.trim() !== ""
  );

const hasHeartbeatExcluded = (filters = {}) =>
  (filters.textExcludes || []).includes(HEARTBEAT_TAG);

const withHeartbeatExclusion = (filters = {}, excludeHeartbeat = false) => {
  const next = { ...filters };
  const currentExcludes = Array.isArray(next.textExcludes) ? [...next.textExcludes] : [];

  if (excludeHeartbeat) {
    if (!currentExcludes.includes(HEARTBEAT_TAG)) {
      next.textExcludes = [...currentExcludes, HEARTBEAT_TAG];
    }
    return next;
  }

  const withoutHeartbeat = currentExcludes.filter((item) => item !== HEARTBEAT_TAG);
  if (withoutHeartbeat.length) {
    next.textExcludes = withoutHeartbeat;
  } else {
    delete next.textExcludes;
  }
  return next;
};

const areArraysEqual = (first = [], second = []) => {
  if (first.length !== second.length) return false;
  return first.every((item, index) => item === second[index]);
};

const areFiltersEqual = (first = {}, second = {}) => {
  const firstKeys = Object.keys(first);
  const secondKeys = Object.keys(second);
  if (firstKeys.length !== secondKeys.length) return false;

  return firstKeys.every((key) => {
    const firstValue = first[key];
    const secondValue = second[key];

    if (Array.isArray(firstValue) || Array.isArray(secondValue)) {
      const normalizedFirst = Array.isArray(firstValue) ? firstValue : [];
      const normalizedSecond = Array.isArray(secondValue) ? secondValue : [];
      return areArraysEqual(normalizedFirst, normalizedSecond);
    }

    return `${firstValue ?? ""}` === `${secondValue ?? ""}`;
  });
};

export const useQuickFixFilterStore = create((set, get) => ({
  filters: {},
  excludeHeartbeat: false,
  hasActiveFilters: false,
  isInitialized: false,
  setFilters: (next) => {
    const sanitized = sanitizeFilters(next);
    const excludeHeartbeat = hasHeartbeatExcluded(sanitized);
    set({
      filters: sanitized,
      excludeHeartbeat,
      hasActiveFilters: hasFilters(sanitized),
      isInitialized: true,
    });
  },
  initializeHeartbeatExclusion: () => {
    const state = get();
    const current = state.filters;
    const withDefaultDateRange = {
      ...current,
      ...(!current.timeFrom && !current.timeTo ? getDefaultPast24hUtcRange() : {}),
    };
    const sanitized = sanitizeFilters(withHeartbeatExclusion(withDefaultDateRange, true));
    const nextHasActiveFilters = hasFilters(sanitized);

    if (
      state.isInitialized &&
      state.excludeHeartbeat === true &&
      state.hasActiveFilters === nextHasActiveFilters &&
      areFiltersEqual(current, sanitized)
    ) {
      return;
    }

    set({
      filters: sanitized,
      excludeHeartbeat: true,
      hasActiveFilters: nextHasActiveFilters,
      isInitialized: true,
    });
  },
  setExcludeHeartbeat: (enabled) => {
    const current = get().filters;
    const sanitized = sanitizeFilters(withHeartbeatExclusion(current, enabled));
    set({
      filters: sanitized,
      excludeHeartbeat: hasHeartbeatExcluded(sanitized),
      hasActiveFilters: hasFilters(sanitized),
      isInitialized: true,
    });
  },
  clearFilters: () =>
    set({ filters: {}, excludeHeartbeat: false, hasActiveFilters: false, isInitialized: true }),
  removeFilter: (key) => {
    const current = { ...get().filters };
    if (key === "minTime" || key === "maxTime") {
      delete current.timeFrom;
      delete current.timeTo;
    } else if (key === "session") {
      // Remove both sender and target when session chip is deleted
      delete current.sendercompid;
      delete current.targetcompid;
    } else if (key.startsWith("textIncludes__")) {
      const idx = parseInt(key.split("__")[1], 10);
      current.textIncludes = (current.textIncludes || []).filter((_, i) => i !== idx);
      if (!current.textIncludes.length) delete current.textIncludes;
    } else if (key.startsWith("textExcludes__")) {
      const idx = parseInt(key.split("__")[1], 10);
      current.textExcludes = (current.textExcludes || []).filter((_, i) => i !== idx);
      if (!current.textExcludes.length) delete current.textExcludes;
    } else if (ARRAY_LIST_KEYS.has(key)) {
      delete current[key];
    } else {
      delete current[key];
    }
    const sanitized = sanitizeFilters(current);
    set({
      filters: sanitized,
      excludeHeartbeat: hasHeartbeatExcluded(sanitized),
      hasActiveFilters: hasFilters(sanitized),
      isInitialized: true,
    });
  },
}));
